/** Cálculos financieros puros (se pueden probar sin interfaz). */

export interface RentInputs {
  precio: number          // precio de compra USD
  costosIniciales: number // escribanía, comisión, impuestos de compra, reformas
  alquiler: number        // alquiler mensual esperado USD
  vacancia: number        // % del año sin alquilar (0–100)
  mantenimiento: number   // gastos comunes + mantenimiento USD/año
  impuestos: number       // contribución, primaria, IRPF u otros USD/año
  administracion: number  // % del alquiler cobrado (0–100)
  crecimiento: number     // % anual de aumento de alquiler y gastos
  revalorizacion: number  // % anual de revalorización del inmueble
  costoVenta: number      // % del precio de venta (comisión, impuestos)
  horizonte: number       // años
  descuento: number       // tasa de descuento % anual
  salida: 'venta' | 'gordon'
}

export interface RentResult {
  ingresoBruto: number
  ingresoEfectivo: number
  noi: number
  rentaBruta: number
  rentaNeta: number
  capRate: number
  flujos: { anio: number; noi: number; salida: number; flujo: number; descontado: number }[]
  valorSalida: number
  van: number
  tir: number | null
  gordonValido: boolean
  inversion: number
}

export function rentabilidad(i: RentInputs): RentResult {
  const v = i.vacancia / 100, a = i.administracion / 100, g = i.crecimiento / 100, r = i.descuento / 100
  const ingresoBruto = i.alquiler * 12
  const ingresoEfectivo = ingresoBruto * (1 - v)
  const noiAnio = (t: number) => {
    const f = Math.pow(1 + g, t - 1)
    return ingresoEfectivo * f * (1 - a) - (i.mantenimiento + i.impuestos) * f
  }
  const noi = noiAnio(1)
  const inversion = i.precio + i.costosIniciales
  const N = Math.max(1, Math.round(i.horizonte))
  const gordonValido = r > g
  const valorSalida = i.salida === 'gordon'
    ? (gordonValido ? noiAnio(N + 1) / (r - g) : NaN)
    : i.precio * Math.pow(1 + i.revalorizacion / 100, N) * (1 - i.costoVenta / 100)

  const flujos = [{ anio: 0, noi: 0, salida: 0, flujo: -inversion, descontado: -inversion }]
  for (let t = 1; t <= N; t++) {
    const n = noiAnio(t)
    const s = t === N && isFinite(valorSalida) ? valorSalida : 0
    const flujo = n + s
    flujos.push({ anio: t, noi: n, salida: s, flujo, descontado: flujo / Math.pow(1 + r, t) })
  }
  const van = flujos.reduce((s, f) => s + f.descontado, 0)
  return {
    ingresoBruto, ingresoEfectivo, noi,
    rentaBruta: i.precio ? ingresoBruto / i.precio : NaN,
    rentaNeta: inversion ? noi / inversion : NaN,
    capRate: i.precio ? noi / i.precio : NaN,
    flujos, valorSalida, van, tir: irr(flujos.map(f => f.flujo)), gordonValido, inversion,
  }
}

/** TIR por bisección entre −99 % y 100 % anual */
export function irr(cf: number[]): number | null {
  const npv = (rate: number) => cf.reduce((s, c, t) => s + c / Math.pow(1 + rate, t), 0)
  let lo = -0.99, hi = 1
  let fLo = npv(lo), fHi = npv(hi)
  if (!isFinite(fLo) || !isFinite(fHi) || fLo * fHi > 0) return null
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2, fm = npv(mid)
    if (Math.abs(fm) < 1e-7) return mid
    if (fLo * fm < 0) { hi = mid; fHi = fm } else { lo = mid; fLo = fm }
  }
  return (lo + hi) / 2
}

/* ------------------------------ P.A.V. ------------------------------ */

export interface PavFactor { key: string; label: string; max: number; score: number; nota?: string }
export interface PavAdjust { label: string; tipo: 'pct' | 'usd'; valor: number }

/**
 * Prestige Adjusted Value
 *   ajuste_i = máximo_i × (puntaje_i − 3) / 2      (puntaje 1–5; 3 = en línea con el mercado)
 *   PAV = VR × (1 + Σ ajuste_i + Σ ajustes %) + Σ ajustes USD
 */
export function pav(valorRef: number, factors: PavFactor[], adjusts: PavAdjust[]) {
  const rows = factors.map(f => ({ ...f, ajuste: (f.max / 100) * (f.score - 3) / 2 }))
  const sumFactors = rows.reduce((s, f) => s + f.ajuste, 0)
  const sumPct = adjusts.filter(a => a.tipo === 'pct').reduce((s, a) => s + a.valor / 100, 0)
  const sumUsd = adjusts.filter(a => a.tipo === 'usd').reduce((s, a) => s + a.valor, 0)
  const total = valorRef * (1 + sumFactors + sumPct) + sumUsd
  return { rows, sumFactors, sumPct, sumUsd, total, prima: valorRef ? total / valorRef - 1 : 0 }
}

/* -------------------------- Confort biofílico -------------------------- */

export const CRITERIOS_BIO = [
  { key: 'luz', label: 'Iluminación natural', desc: 'Cantidad y calidad de luz de día en los ambientes principales.', rec: 'Sumar aberturas o claraboyas, usar cortinas livianas y superficies claras que reflejen la luz.' },
  { key: 'orientacion', label: 'Orientación solar', desc: 'En Uruguay, el norte da sol de invierno y sombra en verano.', rec: 'Proponer aleros o parasoles en fachadas oeste y destacar los ambientes con orientación norte.' },
  { key: 'acustica', label: 'Acústica', desc: 'Ruido exterior y reverberación interior.', rec: 'Evaluar doble vidrio (DVH), burletes y textiles que absorban sonido en los dormitorios.' },
  { key: 'ventilacion', label: 'Ventilación y conexión con el exterior', desc: 'Ventilación cruzada, patios, verde y vistas.', rec: 'Abrir ventilación cruzada, sumar plantas y valorizar patios o balcones como espacios de estar.' },
  { key: 'circadiano', label: 'Confort circadiano y bienestar', desc: 'Luz de mañana en dormitorios, oscuridad nocturna, materiales naturales.', rec: 'Ubicar dormitorios con luz de mañana, cortinas blackout y materiales cálidos como madera y fibras.' },
] as const

export function orientacionScore(o?: string) {
  const map: Record<string, number> = { Norte: 5, Noreste: 5, Noroeste: 4, Este: 4, Oeste: 3, Sureste: 2, Suroeste: 2, Sur: 1 }
  return o ? map[o] ?? 3 : 3
}
export function ruidoScore(db?: number) {
  if (db == null || db === ('' as any)) return 3
  return db <= 35 ? 5 : db <= 45 ? 4 : db <= 55 ? 3 : db <= 65 ? 2 : 1
}
export function bioScore(scores: Record<string, number>) {
  const vals = CRITERIOS_BIO.map(c => scores[c.key] ?? 3)
  const total = Math.round((vals.reduce((s, v) => s + v, 0) / (vals.length * 5)) * 100)
  const label = total >= 85 ? 'Excelente' : total >= 70 ? 'Muy bueno' : total >= 55 ? 'Bueno' : 'A mejorar'
  return { total, label }
}
